import { useI18n } from 'builder/hooks/useI18n'
import { TRANSLATION } from '../../../constants'
import { Service } from '../../../../types'

export type ChangeCareersInfo = {
  badge?: string
  header: string
  subHeader: string
  actionText: string
  bookCoachButton: string
  bookCoachAgain: string
  careerPathButton: string
  assessmentButton: string
  assessmentButtonMobile: string
  careerPathButtonMobile: string
}

type Step = {
  completed?: boolean
  score?: number
}

export type Steps = {
  assessments: Step
  career_path: Step
  book_coach: Step
}

type ChangeCareersConfig = {
  text: ChangeCareersInfo
  steps: Steps
  status: 'initial' | 'completed' | 'progress'
}

export const useChangeCareersConfig = (service: Service): ChangeCareersConfig => {
  const { i18n } = useI18n()
  const percentageProgress = service.calculatedProgress?.percentageProgress || 0

  const getStep = (stepName: string) => {
    return service.data?.steps?.find(step => step.step_name === stepName)
  }

  const getSteps = () => {
    const assessments = getStep('career_assessment_score')
    const careerPath = getStep('build_career_path')
    const bookCoach = getStep('career_coaching')

    return {
      assessments: { completed: assessments?.completed, score: assessments?.target_score },
      career_path: { completed: careerPath?.completed, score: careerPath?.target_score },
      book_coach: { completed: bookCoach?.completed, score: bookCoach?.target_score },
    }
  }

  const getStatus = () => {
    if (percentageProgress >= 100) return 'completed'
    if (service.progress > 0) return 'progress'
    return 'initial'
  }

  const getText = () => {
    const status = getStatus()
    const steps = getSteps()
    const key = `change_careers.${status}`

    if (status === 'progress') {
      return {
        ...(i18n.t(`${TRANSLATION}.${key}`) as unknown as ChangeCareersInfo),
        header: steps.career_path?.completed
          ? i18n.t(`${TRANSLATION}.${key}.header.v2`)
          : i18n.t(`${TRANSLATION}.${key}.header.v1`),
        subHeader: steps.career_path?.completed
          ? i18n.t(`${TRANSLATION}.${key}.subHeader.v2`)
          : i18n.t(`${TRANSLATION}.${key}.subHeader.v1`),
      }
    }

    return i18n.t(`${TRANSLATION}.${key}`) as unknown as ChangeCareersInfo
  }

  return {
    text: getText(),
    steps: getSteps(),
    status: getStatus(),
  }
}
